import React, { useMemo, useState } from "react";

import { Autocomplete, Stack, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { InvestorAutocomplete } from "components/Autocompletes/InvestorAutocomplete";
import { useAccountSelector, useDateFormatSelect } from "store";
import { DealBase } from "store/types";

import { fundFilesTypes as baseFundFilesTypes } from "../../../constants";
import { FileValues } from "./FilesUpload";

const autocompleteSx = {
  "& .MuiOutlinedInput-root": {
    height: 32,
    minHeight: 32,
    "& .MuiInputBase-input": { py: 0 },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#7b92a9" }
  },
  fontSize: 14,
  flex: "33%",
  flexShrink: 0
};

// Multi-select keeps the same fixed 32px height as the single-select controls; instead of growing with
// chips it renders a single summary line that truncates with an ellipsis.
const dealAutocompleteSx = {
  ...autocompleteSx,
  "& .MuiOutlinedInput-root": {
    height: 32,
    minHeight: 32,
    flexWrap: "nowrap",
    overflow: "hidden",
    "& .MuiInputBase-input": { py: 0 },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#7b92a9" }
  }
};

export interface Props {
  selected: Set<number>;
  setFilesValues: React.Dispatch<React.SetStateAction<FileValues[]>>;
  updateDocumentTypeBulk: (indices: Set<number>, value: string | null) => void;
  updateEffectiveDateBulk?: (indices: Set<number>, value: string | null) => void;
  dealInvestors: { id: number; name: string }[];
  dealInvestorsLoading: boolean;
  isCloudMirror?: boolean;
  deals?: DealBase[];
  onBulkDealChange?: (deals: DealBase[]) => void;
  lockedDealId?: number | null;
}

export const BulkActions: React.FC<Props> = ({
  selected,
  setFilesValues,
  updateDocumentTypeBulk,
  updateEffectiveDateBulk,
  dealInvestors,
  dealInvestorsLoading,
  isCloudMirror,
  deals = [],
  onBulkDealChange,
  lockedDealId
}) => {
  const { show_hcs_view } = useAccountSelector() || {};
  const dateFormat = useDateFormatSelect();

  // A file can belong to several deals in cloud mirror, so the bulk picker is multi-select.
  const [bulkDeals, setBulkDeals] = useState<DealBase[]>([]);

  const fundFilesTypes = useMemo(() => {
    return show_hcs_view ? [...baseFundFilesTypes, "Agreement", "Certificate"] : baseFundFilesTypes;
  }, [show_hcs_view]);

  const handleBulkFileTypeChange = (_: React.SyntheticEvent, value: string | null) => {
    updateDocumentTypeBulk(selected, value || null);
  };

  const handleBulkInvestorChange = (value: { id: number; name: string } | null) => {
    setFilesValues((prev) =>
      prev.map((item, i) => (selected.has(i) ? { ...item, investor_id: value?.id ?? null } : item))
    );
  };

  const selectedCount = selected.size;
  const label = selectedCount === 1 ? "1 file selected" : `${selectedCount} files selected`;

  return (
    <Stack
      sx={{
        flexDirection: "row",
        alignItems: "center",
        position: "absolute",
        transform: "translate(-50%, -50%)",
        bottom: 0,
        left: "50%",
        width: "1050px",
        maxWidth: "calc(100% - 40px)",
        bgcolor: "white",
        borderRadius: "8px",
        gap: "20px",
        boxShadow:
          "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
        p: "10px"
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 400,
          color: "#071f35",
          flexShrink: 0,
          minWidth: "fit-content"
        }}
      >
        {label}
      </Typography>
      {isCloudMirror && lockedDealId == null && (
        <Autocomplete<DealBase, true>
          multiple
          disableCloseOnSelect
          fullWidth
          size="small"
          options={deals}
          value={bulkDeals}
          onChange={(_, value) => {
            setBulkDeals(value);
            onBulkDealChange?.(value);
          }}
          getOptionLabel={(opt) => opt.title}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.title}
            </li>
          )}
          renderTags={(value) => (
            <Typography
              component="span"
              sx={{
                fontSize: 14,
                color: "#071f35",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%"
              }}
            >
              {value.map((d) => d.title).join(", ")}
            </Typography>
          )}
          renderInput={(params) => <TextField {...params} placeholder="Deal" />}
          sx={{ ...dealAutocompleteSx, flex: 1, minWidth: 180 }}
        />
      )}
      <Autocomplete<string>
        fullWidth
        size="small"
        options={fundFilesTypes}
        value={null}
        onChange={handleBulkFileTypeChange}
        getOptionLabel={(opt) => opt}
        renderInput={(params) => <TextField {...params} placeholder="Classification" />}
        sx={{ ...autocompleteSx, flex: 1, minWidth: 180 }}
      />
      <InvestorAutocomplete
        sx={{ ...autocompleteSx, flex: 1, minWidth: 200 }}
        label=""
        loading={dealInvestorsLoading}
        dealInvestors={dealInvestors}
        preselectedInvestorId={null}
        onChange={handleBulkInvestorChange}
      />
      {isCloudMirror && (
        <DesktopDatePicker
          value={null}
          onChange={(newValue) =>
            updateEffectiveDateBulk?.(selected, newValue?.isValid() ? newValue.format("YYYY-MM-DD") : null)
          }
          format={dateFormat}
          slotProps={{
            field: { clearable: true, onClear: () => updateEffectiveDateBulk?.(selected, null) },
            textField: { placeholder: "Effective date" }
          }}
          sx={{ ...autocompleteSx, flex: 1, minWidth: 200 }}
        />
      )}
    </Stack>
  );
};
